// Lista «Movimientos» de la pestaña Stock y movimientos: libro del SKU con sus traspasos y ventas.
import { TransferStateEnum } from '@fiado/type-kit/bin/retailCatalog/enums/TransferStateEnum';
import type {
    InventoryMovementEntry,
    InventoryMovementReason,
    InventoryMovementType,
    InventoryStorePieces,
    InventoryStoreReserved,
    TransferResponse,
} from '@/api/services/retail-catalog/backoffice';
import { floorDelta, type SkuSaleRow } from './inventoryDetail';
import { openTransfersOf } from './inventoryMovements';

export type MovementCategory = 'SALE' | 'ENTRY' | 'ADJUSTMENT' | 'TRANSFER' | 'RETURN' | 'HOLD';

/** Apartado y liberación solo cuentan en «Todos». */
export type MovementFilter = 'ALL' | Exclude<MovementCategory, 'HOLD'>;

export type TransferStatus = 'IN_TRANSIT' | 'RECEIVED' | 'CANCELLED';

/** Origen y destino del traspaso; `null` en la tienda que no se sabe. */
export interface TransferInfo {
    fromStoreId: string | null;
    toStoreId: string | null;
    status: TransferStatus | null;
    sentAt: string | null;
    closedAt: string | null;
}

export interface StockMovementRow {
    key: string;
    occurredAt: string;
    category: MovementCategory;
    /** Tipo del asiento más reciente del renglón. */
    type: InventoryMovementType;
    storeId: string;
    createdBy: string;
    identifiers: string[];
    reason?: InventoryMovementReason;
    damaged: boolean;
    /** Piezas en piso que mueve el renglón; `null` si no las mueve. */
    delta: number | null;
    /** Piezas en piso después del renglón; `null` si no las mueve. */
    balance: number | null;
    transfer: TransferInfo | null;
    sale: SkuSaleRow | null;
}

const CATEGORY_OF: Record<InventoryMovementType, MovementCategory> = {
    ENTRY: 'ENTRY',
    RESERVATION: 'HOLD',
    RELEASE: 'HOLD',
    SALE: 'SALE',
    RETURN: 'RETURN',
    ADJUSTMENT: 'ADJUSTMENT',
    TRANSFER_OUT: 'TRANSFER',
    TRANSFER_IN: 'TRANSFER',
    TRANSFER_CANCEL: 'TRANSFER',
};

const STATUS_OF: Record<TransferStateEnum, TransferStatus> = {
    [TransferStateEnum.OPEN]: 'IN_TRANSIT',
    [TransferStateEnum.RECEIVED]: 'RECEIVED',
    [TransferStateEnum.CANCELLED]: 'CANCELLED',
};

/** Lo que dice un asiento de traspaso sin `transferId`: la tienda del asiento y, si cerró, cómo. */
function looseTransferInfo(movement: InventoryMovementEntry): TransferInfo {
    const at = movement.occurredAt;
    if (movement.type === 'TRANSFER_IN') {
        return {
            fromStoreId: null,
            toStoreId: movement.storeId,
            status: 'RECEIVED',
            sentAt: null,
            closedAt: at,
        };
    }
    if (movement.type === 'TRANSFER_CANCEL') {
        return {
            fromStoreId: movement.storeId,
            toStoreId: null,
            status: 'CANCELLED',
            sentAt: null,
            closedAt: at,
        };
    }
    return {
        fromStoreId: movement.storeId,
        toStoreId: null,
        status: null,
        sentAt: at,
        closedAt: null,
    };
}

/** Origen, destino y estado de un traspaso: del TRF# si está en la lista; si no, de sus asientos cargados. */
export function transferInfoOf(
    legs: readonly InventoryMovementEntry[],
    record: TransferResponse | undefined,
): TransferInfo {
    const leg = (type: InventoryMovementType) => legs.find((m) => m.type === type);
    const out = leg('TRANSFER_OUT');
    const received = leg('TRANSFER_IN');
    const cancelled = leg('TRANSFER_CANCEL');
    let status: TransferStatus | null = null;
    if (record) status = STATUS_OF[record.state];
    else if (received) status = 'RECEIVED';
    else if (cancelled) status = 'CANCELLED';
    const closing = received ?? cancelled;
    return {
        fromStoreId: record?.fromStoreId ?? out?.storeId ?? cancelled?.storeId ?? null,
        toStoreId: record?.toStoreId ?? received?.storeId ?? null,
        status,
        sentAt: record?.createdAt ?? out?.occurredAt ?? null,
        closedAt:
            status === 'RECEIVED' || status === 'CANCELLED'
                ? (record?.updatedAt ?? closing?.occurredAt ?? null)
                : null,
    };
}

/** La venta de un asiento SALE por su `saleId`; sin él (ventas viejas) no se empareja. */
export function saleOfMovement(
    movement: InventoryMovementEntry,
    sales: readonly SkuSaleRow[],
): SkuSaleRow | null {
    if (movement.type !== 'SALE' || !movement.saleId) return null;
    return sales.find((s) => s.saleId === movement.saleId) ?? null;
}

/** Piezas en piso hoy: disponibles más apartadas, del consolidado o de la tienda filtrada. */
export function floorStockNow(
    available: number,
    reservedByStore: readonly InventoryStoreReserved[],
    storeId: string | undefined,
): number {
    return available + reservedNow(reservedByStore, storeId);
}

/**
 * Piezas en piso hoy con el saldo que trae la misma respuesta de la ficha; sin saldo en alguna tienda,
 * `fallback` (la cuenta de antes).
 */
export function floorStockFromBalance(
    piecesByStore: readonly InventoryStorePieces[],
    storeId: string | undefined,
    fallback: () => number,
): number {
    const stores = piecesByStore.filter((s) => !storeId || s.storeId === storeId);
    const complete =
        stores.length > 0 &&
        stores.every((s) => s.available !== undefined && s.reserved !== undefined);
    if (!complete) return fallback();
    return stores.reduce((sum, s) => sum + (s.available ?? 0) + (s.reserved ?? 0), 0);
}

/** Unidades apartadas ahora, del consolidado o de la tienda filtrada. */
export function reservedNow(
    reservedByStore: readonly InventoryStoreReserved[],
    storeId: string | undefined,
): number {
    return reservedByStore
        .filter((r) => !storeId || r.storeId === storeId)
        .reduce((sum, r) => sum + r.reserved, 0);
}

export interface MovementListInput {
    movements: readonly InventoryMovementEntry[];
    /** Traspasos de la cadena, para nombrar origen y destino por `transferId`. */
    transfers: readonly TransferResponse[];
    sales: readonly SkuSaleRow[];
    /** Piezas en piso hoy, de donde parte el saldo hacia atrás. */
    floorNow: number;
}

interface Leg {
    movement: InventoryMovementEntry;
    index: number;
    /** Posición en el libro, del más reciente al más viejo. */
    position: number;
    delta: number | null;
    after: number;
}

/**
 * Renglones de la lista, del más reciente al más viejo; los asientos de un mismo `transferId` van
 * en un renglón. El saldo va hacia atrás desde hoy, así que es correcto aunque falten páginas.
 */
export function stockMovementRows(input: MovementListInput): StockMovementRow[] {
    const ordered = input.movements
        .map((movement, index) => ({ movement, index }))
        .sort(
            (a, b) =>
                b.movement.occurredAt.localeCompare(a.movement.occurredAt) || a.index - b.index,
        );
    let floor = input.floorNow;
    const legs: Leg[] = ordered.map(({ movement, index }, position) => {
        const delta = floorDelta(movement);
        const after = floor;
        if (delta !== null) floor -= delta;
        return { movement, index, position, delta, after };
    });
    const groups = new Map<string, Leg[]>();
    for (const leg of legs) {
        const id = transferIdOf(leg.movement);
        if (id) groups.set(id, [...(groups.get(id) ?? []), leg]);
    }
    const records = new Map(input.transfers.map((t) => [t.transferId, t]));
    const rows: StockMovementRow[] = [];
    for (const leg of legs) {
        const id = transferIdOf(leg.movement);
        const group = id ? groups.get(id) : undefined;
        const record = id ? records.get(id) : undefined;
        // Con otro asiento de piso en medio, cada asiento va en su renglón para que el saldo cuadre.
        if (group && interleaved(group, legs)) {
            rows.push(toRow(leg, [leg], record, input.sales, group));
            continue;
        }
        // El traspaso va en la posición de su asiento más reciente, con el saldo de ese momento.
        if (group && group[0] !== leg) continue;
        rows.push(toRow(leg, group ?? [leg], record, input.sales, group ?? [leg]));
    }
    return rows;
}

/** Otro asiento movió el piso entre el primer y el último asiento del traspaso. */
function interleaved(group: readonly Leg[], legs: readonly Leg[]): boolean {
    const newest = group[0].position;
    const oldest = group[group.length - 1].position;
    const members = new Set(group);
    return legs.slice(newest + 1, oldest).some((leg) => !members.has(leg) && leg.delta !== null);
}

function transferIdOf(movement: InventoryMovementEntry): string | undefined {
    return CATEGORY_OF[movement.type] === 'TRANSFER' ? movement.transferId : undefined;
}

function toRow(
    leg: Leg,
    group: readonly Leg[],
    record: TransferResponse | undefined,
    sales: readonly SkuSaleRow[],
    /** Todos los asientos del traspaso, para nombrar origen y destino. */
    transferLegs: readonly Leg[],
): StockMovementRow {
    const { movement } = leg;
    const category = CATEGORY_OF[movement.type];
    const deltas = group.map((g) => g.delta).filter((d): d is number => d !== null);
    const delta = deltas.length === 0 ? null : deltas.reduce((a, b) => a + b, 0);
    const identifiers = [
        ...new Set(group.map((g) => g.movement.identifier).filter((id): id is string => !!id)),
    ];
    let transfer: TransferInfo | null = null;
    if (category === 'TRANSFER') {
        transfer = movement.transferId
            ? transferInfoOf(
                  transferLegs.map((g) => g.movement),
                  record,
              )
            : looseTransferInfo(movement);
    }
    return {
        key:
            movement.movementId ??
            `${movement.occurredAt}-${movement.storeId}-${String(leg.index)}`,
        occurredAt: movement.occurredAt,
        category,
        type: movement.type,
        storeId: movement.storeId,
        createdBy: movement.createdBy,
        identifiers,
        reason: movement.reason,
        damaged: movement.type === 'RETURN' && movement.reason === 'DAMAGED_RETURN',
        delta,
        balance: delta === null ? null : leg.after,
        transfer,
        sale: saleOfMovement(movement, sales),
    };
}

/** Conteo de cada chip; «Todos» incluye apartados y liberaciones. */
export function movementCounts(rows: readonly StockMovementRow[]): Record<MovementFilter, number> {
    const counts: Record<MovementFilter, number> = {
        ALL: rows.length,
        SALE: 0,
        ENTRY: 0,
        ADJUSTMENT: 0,
        TRANSFER: 0,
        RETURN: 0,
    };
    for (const row of rows) if (row.category !== 'HOLD') counts[row.category] += 1;
    return counts;
}

export function filterMovements(
    rows: readonly StockMovementRow[],
    filter: MovementFilter,
): StockMovementRow[] {
    return filter === 'ALL' ? [...rows] : rows.filter((r) => r.category === filter);
}

/** Piezas del SKU en traspasos abiertos; con tienda, las que salen de ella o van hacia ella. */
export function inTransitQty(
    transfers: readonly TransferResponse[],
    sku: string,
    storeId: string | undefined,
): number {
    return openTransfersOf(transfers, sku)
        .filter((t) => !storeId || t.fromStoreId === storeId || t.toStoreId === storeId)
        .reduce((sum, t) => sum + t.qty, 0);
}

/** Piezas en tránsito que recibirá cada tienda destino. */
export function inboundInTransit(
    transfers: readonly TransferResponse[],
    sku: string,
): Map<string, number> {
    const byStore = new Map<string, number>();
    for (const t of openTransfersOf(transfers, sku)) {
        byStore.set(t.toStoreId, (byStore.get(t.toStoreId) ?? 0) + t.qty);
    }
    return byStore;
}
